import {notification} from 'antd';
import {LOGIN_WITH_REDIRECT_ROUTE} from "../routes/route-path";
import StorageService from "./StorageService";
import {AxiosError} from "axios";
import TokenService from "./TokenService";
import moment from "jalali-moment";
import {Counter, ModeOfOperation} from 'aes-js';
import {Buffer} from 'buffer';
import cookie, {CookieAttributes} from "js-cookie";
import UserService from "./UserService";

interface Errors {
    field: string;
    message: string;
}

export interface Pageable {
    pageNumber: number;
}

export interface Page<T> {
    content: T[];
    totalElements: number;
    number: number;
    size: number;
    last: boolean;
    pageable: Pageable;
}

export interface PageQueryParams {
    pageNumber: number;
    pageSize: number;
}

export function handleError(error: any) {
    if (error.response.status === 401) {
        StorageService.removeItem("token");
        StorageService.removeItem("roles");
        StorageService.removeItem("profile");
        document.location.href = LOGIN_WITH_REDIRECT_ROUTE;
    } else if (error.response.status >= 400 && error.response.status < 500) {
        if (error.response.data.message) openErrorNotification(error.response.data.message);
        if (error.response.data.errors) {
            error.response.data.errors.forEach((elem: Errors) => {
                openErrorNotification(elem.message)
            })
        }
    } else if (error.response.status >= 500) {
        openErrorNotification("خطایی سمت سرور رخ داده است.")
    }
}

export const openErrorNotification = (message: string, duration?: number) => {
    notification.error({
        message: 'خطا',
        description: message,
        placement: "top",
        className: 'notification-messages',
        duration: duration || duration === 0 ? duration : 10
    });
}
export const openSuccessNotification = () => {
    notification.success({
        message: '',
        description: 'عملیات با موفقیت انجام شد.',
        placement: "top",
        duration: 5
    });
}

export const numberWithCommas = (x: any) => {
    return x && x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const numberWithCommasToman = (x: any) => {
    let number = numberWithCommas(x);
    return number ? number + ' تومان' : number;
}

export const numberWithCommasTomanAndIncludeZero = (x: any) => {
    let number = numberWithCommas(x);
    return number || number === 0 ? number + ' تومان' : number;
}

export const openSuccessNotificationByMessage = (msg: string, duration?: number) => {
    notification.success({
        message: '',
        description: msg,
        placement: "top",
        duration: duration || duration === 0 ? duration : 10
    });
}

export async function genericApiCall<T>(api: Function): Promise<T> {
    try {
        return await api();
    } catch (error) {
        const err = error as AxiosError;
        if (err.response?.status === 401) {
            await TokenService.updateToken();
            return await api();
        }
        handleError(error);
        throw error;
    }
}

export function toPersianDateTime(input: Date): string | null {
    if (input) return moment(input, 'YYYY-MM-DDTHH:mm:ss').locale('fa').format('HH:mm:ss YYYY/MM/DD');
    return null;
}

export function toPersianDateTime2(input: Date): string | null {
    if (input) return moment(input, 'YYYY-MM-DDTHH:mm:ss').locale('fa').format('YYYY/MM/DD HH:mm');
    return null;
}

export function toPersianDate(input: Date): string | null {
    if (input) return moment(input, 'YYYY-MM-DDTHH:mm:ss').locale('fa').format('YYYY/MM/DD');
    return null;
}

const keyBytes = Buffer.from('my_super_secret_', 'utf-8');

export function encrypt(input: string): string {
    const bytes = Buffer.from(input, 'utf-8');
    const aesEcb = new ModeOfOperation.ctr(keyBytes, new Counter(5));
    const encryptedBytes = aesEcb.encrypt(bytes);
    const buffer = Buffer.from(encryptedBytes.buffer);
    return buffer.toString('base64');
}

export function decrypt(input: string): string | null {
    const encryptedBytes = Buffer.from(input, 'base64');
    const aesEcb = new ModeOfOperation.ecb(keyBytes);
    const decryptedBytes = aesEcb.decrypt(encryptedBytes);
    const paddingLength = decryptedBytes[decryptedBytes.length - 1];
    const paddedData = decryptedBytes.subarray(0, decryptedBytes.length - paddingLength);
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(paddedData);
}

export function toEnglishNumber(input: string): string {
    const pn = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
    const en = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
    let cache = input;
    for (let i = 0; i < 10; i++) {
        let regex_fa = new RegExp(pn[i], 'g');
        cache = cache.replace(regex_fa, en[i]);
    }
    return cache;
}

export function getLocalDateTime(date: Date): string | undefined {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    // Format date string
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

export function setCookie(name: string, value: string, options: CookieAttributes) {
    if (process.env.NODE_ENV === 'development') {
        cookie.set(name, value, {
            ...options,
            secure: false
        });
    } else if (process.env.NODE_ENV === 'production') {
        cookie.set(name, value, {
            ...options
        });
    }
}

export function testConnection() {
    if (TokenService.isLogin()){
        const image = new Image();
        const imageSizeInBytes = 53666; // adjust with your image size

        const startTime = new Date().getTime();
        image.src = "https://api.daneshgahino.org/public/v1/files/download/91bf6695-1b01-4891-badd-93863b88f5c2/test3.png?t=" + startTime; // should be a cache-busting image URL

        image.onload = () => {
            const endTime = new Date().getTime();
            const duration = (endTime - startTime) / 1000;
            const bitsLoaded = imageSizeInBytes * 8;
            const speedBps = bitsLoaded / duration;
            const speedMbps = (speedBps / (1024 * 1024)).toFixed(2);
            UserService.sendConnectionInfo(JSON.stringify({ speedMbps, latencyMs: duration * 1000 }));
        };
        image.onerror = () => {
            UserService.sendConnectionInfo(JSON.stringify({ speedMbps: 0, latencyMs: 0 }));
        };
    }
}